/**
 * App shell: owns the single ApiClient instance, the nurse auth state
 * machine (GET /api/auth/state -> screenForAuthState, state.ts), path
 * routing (/ , /ops , /dev/mailbox, checked once at boot, no router
 * library), and the active screen's header/tabs/lock/idle-nudge, the
 * auto-lock and the keepalive (T2075 M6, the idle triple below).
 *
 * Every screen receives the same `api` instance, so the 401-refetch
 * behaviour wired in api.ts (onUnauthorized -> reloadAuthState) applies
 * everywhere without each screen repeating it.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { createApiClient, type ApiClient } from './api.js';
import { screenForAuthState, routeForPath, isAuthState, tabsForRole, defaultTabForRole, type AuthState, type ActiveTab } from './state.js';
import { SESSION_IDLE_MS } from '../shared/session.js';
import { Enrol } from './screens/Enrol.js';
import { PinSetup } from './screens/PinSetup.js';
import { Picker } from './screens/Picker.js';
import { RequestForm, type RequestFormDraft, type RequestSentFacts, type SendPhase } from './screens/RequestForm.js';
import { MyRequests, LIST_REFRESH_MS } from './screens/MyRequests.js';
import { MyResidents } from './screens/MyResidents.js';
import { HomeRequests } from './screens/HomeRequests.js';
import { Staff } from './screens/Staff.js';
import { Ops } from './screens/Ops.js';
import { DevMailbox } from './screens/DevMailbox.js';
import { shellCopy, sharedCopy, lockFailedCopy } from './copy.js';

/** The strip's words, keyed by tab — one map, so a tab cannot be offered
 *  without a label or labelled without being a tab (`ActiveTab` is exhaustive). */
const TAB_LABELS: Record<ActiveTab, string> = {
  'new-request': shellCopy.tabNewRequest,
  'my-requests': shellCopy.tabMyRequests,
  'my-residents': shellCopy.tabMyResidents,
  'home-requests': shellCopy.tabRequests,
  staff: shellCopy.tabStaff,
};
import { Button, Shell } from '@ecmb/ui';

/**
 * ⭐⭐ T2075 M6 (board 2074, R-17; plan A1, D14, S15) — THE SHARED-PC IDLE
 * TRIPLE. One shared-PC nurse station, one signed-in session, and a nurse who
 * walks away: the screen NUDGES at 540 s, LOCKS ITSELF at 600 s (the session
 * revoked, the Picker rendered, no resident name left in the DOM), and the
 * server forgets a session it has not heard from for 660 s (board 2706: the
 * figures were 60 / 120 / 300 s until LCC went live and a nurse lost a nearly
 * finished booking to the lock; Alex set the lock)
 * (`SESSION_IDLE_MS`, `shared/session.ts` — the SAME constant the server
 * read enforces, imported here so the two cannot drift). A nurse who is
 * TYPING is never interrupted: a keepalive `GET /api/auth/state` every 120 s
 * while she is active and not idle, plus ONE at the moment she returns from
 * idle, slides `last_active_at` (every authenticated request does, through
 * `resolveAuth`), so a six-minute Notes draft still sends into a live
 * session; an unattended tab makes no request after the nudge, which is the
 * 2073 D8 rule this triple keeps.
 *
 * ⛔ THE BOUND (D14), asserted at import by `assertIdleBound` below:
 *   (1) IDLE_NUDGE_MS + LIST_REFRESH_MS < IDLE_LOCK_MS < SESSION_IDLE_MS
 *   (2) KEEPALIVE_MS < SESSION_IDLE_MS − LIST_REFRESH_MS
 *   (3) KEEPALIVE_MS + IDLE_LOCK_MS − IDLE_NUDGE_MS < SESSION_IDLE_MS
 * (1) says the nudge (and the last list tick it can still gate) lands before
 * the lock, and the lock before the server's own cut-off, so the client
 * always locks a session the server still holds. (2) says a keepalive tick
 * always reaches the server inside its window even if a list tick was the
 * last thing to slide it. (3) is the RESTART GAP (security-review Low 3, the
 * implementer's finding (ii)): the interval is cleared at the nudge and
 * restarted on the return from idle, so without the return GET the longest
 * silence was 2·K + L − N (which EQUALLED SESSION_IDLE_MS at the 2074
 * figures) — a nurse who paused just short of a tick and came back just short
 * of the lock typed on into a 401.
 * With the return GET the silence after a contact at C is bounded by: she
 * idles at C + x (x < K, the tick at C + K is cleared), her last touch was
 * C + x − N, the lock is due at C + x − N + L, and any return before it
 * fires a GET at once — so the gap is < K + L − N = 180 s (never idle: K).
 * A figure that breaks any of the three throws here, at import, so the
 * bundle cannot build with a triple that lies.
 *
 * ⚠️ THE MASKING THE OUTPUT GATE NAMED, AND WHY NO INEQUALITY ENCODES IT.
 * In the first cut, dropping the keepalive's `idle` CONDITION alone (the dep
 * kept) was invisible while the lock succeeded: the effect re-ran on the
 * idle flip and RESTARTED the interval, whose first tick (idle + K) landed
 * after the lock (idle + L − N = 60 s) because L − N < K today. That was a
 * coincidence of the figures, not a requirement, and it no longer holds at
 * all: with the return-from-idle GET the same mutant fires a GET AT THE
 * NUDGE (the flip runs the return branch), so it reds M6-5, M6-7 and M6-9
 * outright (measured 2026-09-15). The D8 rule — no request from an
 * unattended tab — is graded directly on both the succeeding-lock path
 * (M6-5) and the failing-lock path (M6-7, where no lock ever ends the
 * silence); there is nothing left for an inequality to keep from being lost.
 *
 * ⭐⭐ BOARD 2706 — WHAT A TEN-MINUTE LOCK CHANGED, AND THE THREE THINGS IT ADDED.
 *   · SLEEP. A device that sleeps pauses its timers, which at 600 s turns a
 *     small gap into the main exposure. So the idle hook also judges by the
 *     wall clock, but ONLY for a deadline the timers MISSED (`useIdleNudge`):
 *     a touch, a periodic check (`IDLE_CHECK_MS`) or a visibility change locks
 *     once `lastActivityAt + IDLE_LOCK_MS` has passed. A lock the timer already
 *     fired keeps the M6 revive-on-touch path, so the stale-generation rule
 *     below is unchanged. While a lock is due the shell renders a blank branded
 *     COVER, which hides the data during the lock's round trip and stops a
 *     waking tap landing on Send (pointerdown and click are separate tasks, so
 *     the cover commits in between).
 *   · THE DRAFT. A lock no longer loses the New request form: the form reports
 *     its unsaved work, the lock unmounts it exactly as before (DOM clean), the
 *     shell holds the draft in memory only, and a PIN unlock by the SAME account
 *     within `DRAFT_HOLD_MS` (judged by the wall clock) restores it.
 *   · A NEW DEPLOY. The server names the client build it serves (`x-portal-build`
 *     on every API answer, `<meta name="portal-build">` in the page); when the
 *     two differ the shell reloads onto the new build, but only at a rest point
 *     with no unsaved work (`reloadTarget` in `NurseApp`).
 */
export const IDLE_NUDGE_MS = 540_000;
export const IDLE_LOCK_MS = 600_000;
export const KEEPALIVE_MS = 120_000;
/** Board 2706 — how often the idle hook re-reads the wall clock for a deadline
 *  the timers missed (they pause while a device sleeps). */
export const IDLE_CHECK_MS = 15_000;
/** Board 2706 (plan D4) — how long a held New request draft can be restored
 *  after the lock took it off the screen; judged by the wall clock at restore. */
export const DRAFT_HOLD_MS = 60 * 60 * 1000;
/** Board 2706 (plan D5, security review Low 4) — after a build reload, how long the new page
 *  makes no further build reload, judged by the wall clock against the `at` the reload URL
 *  carried. It ends the bounce a deploy overlap can cause (page B answered by instance A reloads
 *  to A, page A answered by B reloads to B, for as long as both are served). A cooldown rather
 *  than a hop count, because hops would accumulate across legitimate deploys in an app left open
 *  for weeks. */
export const BUILD_RELOAD_COOLDOWN_MS = 10 * 60 * 1000;
/** How soon a failed auto-lock POST is retried while the screen is still
 *  due to lock (security-review Med 1). The failed attempt already took the
 *  data off the screen (the fail-closed error screen); this is how long the
 *  session it could not revoke stays live before the next try. */
export const LOCK_RETRY_MS = 15_000;
export { SESSION_IDLE_MS };

export type IdleTriple = {
  nudgeMs: number;
  lockMs: number;
  keepaliveMs: number;
  sessionIdleMs: number;
  listRefreshMs: number;
};

/** The D14 bound as a function, so the spec can prove the tripwire bites on
 *  a violating triple as well as pass the live one; the module-level call
 *  below is what makes it an import-time check. */
export function assertIdleBound(t: IdleTriple): void {
  if (!(t.nudgeMs + t.listRefreshMs < t.lockMs && t.lockMs < t.sessionIdleMs)) {
    throw new Error(
      `idle triple out of bound (D14): need IDLE_NUDGE_MS + LIST_REFRESH_MS < IDLE_LOCK_MS < SESSION_IDLE_MS, ` +
        `got ${String(t.nudgeMs)} + ${String(t.listRefreshMs)} / ${String(t.lockMs)} / ${String(t.sessionIdleMs)}`,
    );
  }
  if (!(t.keepaliveMs < t.sessionIdleMs - t.listRefreshMs)) {
    // ⚠️ No spaced hyphen in the sentence: check-copy.sh's clause rule reads
    // every literal in this tree, an Error message included.
    throw new Error(
      `idle triple out of bound (D14): need KEEPALIVE_MS < SESSION_IDLE_MS minus LIST_REFRESH_MS, ` +
        `got ${String(t.keepaliveMs)} / ${String(t.sessionIdleMs)} minus ${String(t.listRefreshMs)}`,
    );
  }
  if (!(t.keepaliveMs + t.lockMs - t.nudgeMs < t.sessionIdleMs)) {
    throw new Error(
      `idle triple out of bound (D14, the restart gap): need KEEPALIVE_MS + IDLE_LOCK_MS minus IDLE_NUDGE_MS < SESSION_IDLE_MS, ` +
        `got ${String(t.keepaliveMs)} + ${String(t.lockMs)} minus ${String(t.nudgeMs)} / ${String(t.sessionIdleMs)}`,
    );
  }
}

assertIdleBound({
  nudgeMs: IDLE_NUDGE_MS,
  lockMs: IDLE_LOCK_MS,
  keepaliveMs: KEEPALIVE_MS,
  sessionIdleMs: SESSION_IDLE_MS,
  listRefreshMs: LIST_REFRESH_MS,
});

export function App() {
  const route = routeForPath(window.location.pathname);
  if (route === 'ops') return <OpsRoute />;
  if (route === 'dev-mailbox') return <DevMailboxRoute />;
  return <NurseApp />;
}

function OpsRoute() {
  const apiRef = useRef<ApiClient | null>(null);
  apiRef.current ??= createApiClient();
  return (
    <Shell variant="bar" label={shellCopy.brandLabel}>
      <main>
        <Ops api={apiRef.current} />
      </main>
    </Shell>
  );
}

function DevMailboxRoute() {
  const apiRef = useRef<ApiClient | null>(null);
  apiRef.current ??= createApiClient();
  return (
    <Shell variant="bar" label={shellCopy.brandLabel}>
      <main>
        <DevMailbox api={apiRef.current} />
      </main>
    </Shell>
  );
}

/** `idle` after `nudgeMs` and `lockDue` after `lockMs` of no pointer/key
 *  activity, BOTH reset by the same pointerdown/keydown (T2075 M6: two timers,
 *  one reset, so a touch that cancels the nudge always cancels the lock too).
 *  `idle` is a UX signal (the banner, the list's poll gate); `lockDue` is the
 *  shell's cue to revoke the session (`lock()`), and the server's own idle
 *  window (`SESSION_IDLE_MS`) remains the enforcement behind both, so a stale
 *  timer here is a late or early banner and a late or early lock, never an
 *  open session the server would not also have ended.
 *
 *  ⭐⭐ Board 2706 (plan D3) — AND THE WALL CLOCK, FOR A DEADLINE THE TIMERS
 *  MISSED. Timers pause while a device sleeps, so a tablet put down for an hour
 *  wakes with its lock timer still minutes away. `lastActivityAt` is the wall
 *  time of the last arm; once it is `lockMs` old and the lock timer has NOT
 *  fired (`lockFired`), a touch LOCKS instead of reviving, and the periodic
 *  check plus `visibilitychange` (`catchUp`) catch a woken screen nobody
 *  touched.
 *  ⛔ ONLY WHILE THE TIMER HAS NOT FIRED. Once it has, a touch keeps the M6
 *  revive path (the lock POST may already be in flight, and its stale-generation
 *  handling in `NurseApp` decides the outcome), and `lockFired` is cleared on
 *  EVERY re-arm, so a fired timer, a touch and a failed lock POST cannot leave
 *  the wall-clock rule off for the rest of the session. */
function useIdleNudge(active: boolean, nudgeMs: number, lockMs: number): { idle: boolean; lockDue: boolean } {
  const [idle, setIdle] = useState(false);
  const [lockDue, setLockDue] = useState(false);

  useEffect(() => {
    if (!active) {
      setIdle(false);
      setLockDue(false);
      return;
    }
    let nudgeTimer: ReturnType<typeof setTimeout> | undefined;
    let lockTimer: ReturnType<typeof setTimeout> | undefined;
    let lastActivityAt = Date.now();
    let lockFired = false;
    /** The lock is due now, by the wall clock rather than by a timer. */
    const fire = (): void => {
      lockFired = true;
      clearTimeout(nudgeTimer);
      clearTimeout(lockTimer);
      setIdle(true);
      setLockDue(true);
    };
    const arm = (): void => {
      lockFired = false;
      lastActivityAt = Date.now();
      setIdle(false);
      setLockDue(false);
      clearTimeout(nudgeTimer);
      clearTimeout(lockTimer);
      nudgeTimer = setTimeout(() => setIdle(true), nudgeMs);
      lockTimer = setTimeout(() => {
        lockFired = true;
        setLockDue(true);
      }, lockMs);
    };
    const missed = (): boolean => !lockFired && Date.now() - lastActivityAt >= lockMs;
    const reset = (): void => {
      if (missed()) fire();
      else arm();
    };
    const catchUp = (): void => {
      if (lockFired) return;
      if (missed()) fire();
      else if (Date.now() - lastActivityAt >= nudgeMs) setIdle(true);
    };
    arm();
    const check = setInterval(catchUp, IDLE_CHECK_MS);
    window.addEventListener('pointerdown', reset);
    window.addEventListener('keydown', reset);
    document.addEventListener('visibilitychange', catchUp);
    return () => {
      clearTimeout(nudgeTimer);
      clearTimeout(lockTimer);
      clearInterval(check);
      window.removeEventListener('pointerdown', reset);
      window.removeEventListener('keydown', reset);
      document.removeEventListener('visibilitychange', catchUp);
    };
  }, [active, nudgeMs, lockMs]);

  return { idle, lockDue };
}

/** The New request form's unsaved work, held by the shell (board 2706, plan D4):
 *  a `draft`, a draft whose send is in flight (`sending`), or the outcome of a
 *  send that settled while the form was off the screen (`sent`; a failed send
 *  goes back to `draft`, still sendable). `heldAt` is the wall time the live form
 *  left the screen, `null` while it is on the screen. */
type HeldDraft = { accountId: number; heldAt: number | null } & (
  | { phase: 'draft' | 'sending'; draft: RequestFormDraft }
  | { phase: 'sent'; sent: RequestSentFacts }
);

/**
 * ⭐ Board 2706 rework (R5) — after a sign-in, put focus on the active screen's
 * heading as soon as it renders (screens render it only once their data has
 * loaded), so a keyboard or screen-reader user starts on the page she landed on
 * rather than on the document body the Picker left behind. A heading with no
 * `tabindex` is made a legal programmatic target first (-1: never in the tab
 * order). ⛔ NEVER STEALS FOCUS: once anything other than the body has focus, it
 * stops. Returns the cleanup that stops watching.
 * ⛔ AND IT STOPS WATCHING SOON (board 2706 rework, round 2, output gate): Safari
 * does not focus a clicked button, so `activeElement` can stay the body while she
 * is already working, and a watcher left connected would pull focus onto a heading
 * that rendered later. It ends at the first focus, the first pointerdown or
 * keydown (she has started), or `FOCUS_HEADING_WAIT_MS`, whichever comes first.
 */
export const FOCUS_HEADING_WAIT_MS = 2_000;
function focusHeadingWhenShown(): () => void {
  const tryFocus = (): boolean => {
    const current = document.activeElement;
    if (current !== null && current !== document.body) return true;
    const heading = document.querySelector<HTMLElement>('main h1');
    if (heading === null) return false;
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus();
    return true;
  };
  if (tryFocus()) return () => {};
  const watcher = new MutationObserver(() => {
    if (tryFocus()) stop();
  });
  const deadline = setTimeout(() => stop(), FOCUS_HEADING_WAIT_MS);
  function stop(): void {
    watcher.disconnect();
    clearTimeout(deadline);
    window.removeEventListener('pointerdown', stop, true);
    window.removeEventListener('keydown', stop, true);
  }
  watcher.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pointerdown', stop, true);
  window.addEventListener('keydown', stop, true);
  return stop;
}

/**
 * ⭐ Board 2706 (plan D5) — the one navigation this shell performs, as a seam:
 * jsdom has no navigation, so a spec spies on `navigation.replace`.
 */
export const navigation = {
  replace(url: string): void {
    window.location.replace(url);
  },
};

/**
 * ⭐ Board 2706 (plan D5) — read ONCE per page: the build this page was served
 * with (the meta `http.ts` injects), and the three query params a build reload
 * carries, stripped at once so a later reload or a bookmark never repeats them.
 * `build` names the target the previous page reloaded for and is only stripped
 * (the guard is the cooldown below); `at` is the wall time of that reload (the
 * cooldown; anything but digits is treated as absent); `tab` is where that reload
 * left her.
 */
function readBoot(): {
  pageBuild: string | null;
  reloadedAt: number | null;
  tab: string | null;
} {
  const pageBuild = document.querySelector('meta[name="portal-build"]')?.getAttribute('content') || null;
  const url = new URL(window.location.href);
  const reloadedFor = url.searchParams.get('build');
  const at = url.searchParams.get('at');
  // ⛔ A FUTURE `at` IS NO `at`, decided HERE at boot (security review round 2): judged only at
  // each rest point it would start suppressing reloads once the clock reached it, i.e. a crafted
  // link could choose WHEN the cooldown begins.
  const parsedAt = at !== null && /^\d{1,16}$/.test(at) ? Number(at) : null;
  const reloadedAt = parsedAt !== null && parsedAt <= Date.now() ? parsedAt : null;
  const tab = url.searchParams.get('tab');
  if (reloadedFor !== null || at !== null || tab !== null) {
    url.searchParams.delete('build');
    url.searchParams.delete('at');
    url.searchParams.delete('tab');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
  return { pageBuild, reloadedAt, tab };
}

function NurseApp() {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [enrolNote, setEnrolNote] = useState<string | null>(null);
  // Local-only navigation override for Picker's "Different nurse? Sign in
  // with email code" link: the device is still 'locked' server-side (no
  // state change), so screenForAuthState alone would keep returning
  // 'picker' forever. Cleared automatically the moment a fresh auth-state
  // load resolves to anything other than 'locked' (see reloadAuthState).
  const [forceEnrol, setForceEnrol] = useState(false);
  const [tab, setTab] = useState<ActiveTab>('new-request');
  /** ⭐ Board 2706 — the page's build and the reload params, read once (`readBoot`). */
  const boot = useRef<ReturnType<typeof readBoot> | null>(null);
  boot.current ??= readBoot();
  /**
   * ⭐⭐ T2173 rework 1 — ON AN IDENTITY CHANGE, GO TO THAT ROLE'S DEFAULT TAB.
   *
   * ⛔ `tab` IS SHELL STATE, NOT SESSION STATE, and on a device the whole home
   * shares that is the bug: it survived the person. Captain's live drive locked
   * an admin sitting on `Staff`, signed a group nurse in, and landed her on a
   * screen her role cannot load — THREE 403s (`/api/admin/staff`,
   * `/api/admin/accounts`, `/api/home/requests`) behind one "Could not load…".
   * `tabsForRole` was already right; nothing moved her off the dead tab.
   *
   * ⛔ KEYED ON THE NURSE ID, NOT ON `role` OR ON `auth`. On the id it fires
   * exactly once per identity — including the null -> id edge of a fresh sign-in,
   * and again after a lock, which is correct (a new session starts at the
   * default). Keyed on `role` instead, two group nurses swapping would not fire
   * at all and the second would inherit the first's tab. Keyed on `auth`, every
   * poll would fire it and nobody could ever leave the default tab.
   */
  const activeNurseId = auth !== null && auth.state === 'active' ? auth.nurse.id : null;
  const activeNurseRole = auth !== null && auth.state === 'active' ? auth.nurse.role : null;
  /** ⛔ THE LAST IDENTITY SEEN, HELD ACROSS THE LOCK. A lock takes
   *  `activeNurseId` to null and a PIN unlock brings the SAME id back, so
   *  comparing against the live value alone would treat every unlock as a new
   *  person and dump her on the default tab — measured: `portal-smoke`'s "PIN
   *  re-login restores the request list" went red on exactly that. A ref, not
   *  state, because nothing renders from it. */
  const lastIdentity = useRef<number | null>(null);
  useEffect(() => {
    if (activeNurseId === null || activeNurseRole === null) return;
    // ⛔ ONLY ON A CHANGE. Same person back from the lock keeps her tab; a
    // DIFFERENT person gets her own role's default, which is the whole fix.
    if (lastIdentity.current === activeNurseId) return;
    const first = lastIdentity.current === null;
    lastIdentity.current = activeNurseId;
    // ⭐ Board 2706 (plan D5) — a build reload carries the tab it left (`?tab=`), honoured for
    // the page's FIRST identity only and only when that role is offered the tab, so the
    // reload can never land anybody on a screen her role cannot load (the rule above).
    const bootTab = boot.current?.tab ?? null;
    if (boot.current !== null) boot.current.tab = null;
    const allowed = tabsForRole(activeNurseRole);
    const wanted = allowed.find((t) => t === bootTab);
    setTab(first && wanted !== undefined ? wanted : defaultTabForRole(activeNurseRole));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the ID is the identity; see above.
  }, [activeNurseId]);
  /**
   * ⭐ II-portal-#27 (T1723 M4) — `New request` stayed `aria-pressed="true"` on
   * the "Request sent" panel, i.e. the tab bar kept describing a screen that was
   * no longer on show. The panel is RequestForm's own state, so the shell is
   * told rather than made to guess (`onSentChange`), and while it is up NO tab
   * is pressed, because none of the three is what the nurse is looking at.
   * ⚠️ Reset by the report itself, IN BOTH DIRECTIONS: RequestForm fires `false`
   * when the panel goes (`Another request`, a re-mount on re-entering this tab)
   * AND from its effect cleanup when it unmounts, so there is no second flag
   * here to forget to clear.
   * ⛔ THE CLEANUP IS LOAD-BEARING, not tidiness (M4 output gate). Without it,
   * switching to `My requests` or `My residents` off the sent panel unmounted
   * the only component that could ever report `false` - and this flag stayed
   * true for the rest of the session, so every screen after it rendered with no
   * tab pressed at all. The shell must not try to guess it back: `success` is
   * RequestForm's own state and nothing here can see it.
   */
  const [requestSent, setRequestSent] = useState(false);

  /**
   * ⭐⭐ Board 2706 (plan D4) — THE HELD DRAFT. ONE definition of unsaved work, used by both the
   * keep and the reload: RequestForm reports `{values, residentQuery, fieldAuthor}` while it has
   * any (`onDraftChange`), `null` when pristine or showing "Request sent". A REF, because nothing
   * renders from it; `formHasDraft` is the one bit rest point B must re-run on.
   * ⛔ MEMORY ONLY. The lock still unmounts the form (no resident name left in the DOM) and there
   * is no browser storage anywhere in this client; the draft lives in this ref and nowhere else.
   * ⛔ IT IS DROPPED by a DIFFERENT account becoming active, an actual tab change, a send, and the
   * hold (`DRAFT_HOLD_MS`, judged by the wall clock at restore because the drop timer pauses in a
   * sleeping device like every other timer). ⭐ Board 2706 rework (R3): NOT by the email route
   * (`forceEnrol`), a lost device cookie (`no_device`) or PIN setup (`needs_pin`) — those are how
   * the SAME nurse gets back in when her PIN fails her, and she lands active with her work.
   * ⭐ Board 2706 rework (R1): a send in flight and its outcome are held here too (`phase`), and
   * count as unsaved work for both reload rest points.
   */
  const draftRef = useRef<HeldDraft | null>(null);
  const dropTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [formHasDraft, setFormHasDraft] = useState(false);
  const dropDraft = (): void => {
    clearTimeout(dropTimerRef.current);
    draftRef.current = null;
    setFormHasDraft(false);
  };
  /** The held draft, or null once its hold has run out by the wall clock. */
  const heldDraft = (): HeldDraft | null => {
    const held = draftRef.current;
    if (held !== null && held.heldAt !== null && Date.now() - held.heldAt >= DRAFT_HOLD_MS) {
      dropDraft();
      return null;
    }
    return held;
  };
  useEffect(() => () => clearTimeout(dropTimerRef.current), []);

  const apiRef = useRef<ApiClient | null>(null);
  const reloadAuthStateRef = useRef<() => void>(() => {});
  /** ⭐ Board 2706 (plan D5) — the build the server last named: a ref for the rest point A
   *  check inside `reloadAuthState` (the state below lands a render later), state for B. */
  const serverBuildRef = useRef<string | null>(null);
  const [serverBuild, setServerBuild] = useState<string | null>(null);
  apiRef.current ??= createApiClient({
    onUnauthorized: () => reloadAuthStateRef.current(),
    onBuild: (id) => {
      serverBuildRef.current = id;
      setServerBuild(id);
    },
  });
  const api = apiRef.current;

  /**
   * ⭐⭐ Board 2706 (plan D5) — THE BUILD TO RELOAD ONTO, or null. An app copy open since before
   * a deploy keeps the old bundle, and the old bundle's requests are what the new server refuses
   * (George's finished booking met "This page is out of date"). So the shell reloads onto the
   * build the server names, but ONLY at a rest point with no unsaved work (`reloadOnto`'s callers).
   * ⛔ NEVER WITHOUT BOTH IDS: a page with no meta or a server with no header is not evidence of a
   * new build, so a missing id on either side never reloads.
   * ⛔ THE LOOP GUARD IS A COOLDOWN (security review Low 4): during a deploy overlap two
   * instances can bounce a page between them (B answered by A, then A answered by B), so the
   * reload URL carries `at=<wall time>` and the page it lands on makes no build reload until
   * `BUILD_RELOAD_COOLDOWN_MS` has passed since. An `at` in the future (a device clock moved
   * back) is ignored rather than honoured, so it can never block reloads for longer than the
   * cooldown (and `readBoot` drops an `at` that is still in the future at boot, so it cannot
   * start a cooldown later either). This page itself never reloads twice for one target
   * (`reloadedForRef`).
   * ⛔ NO PER-TARGET GUARD ACROSS PAGES: a page that landed on the OLD instance during the overlap
   * must retry the new build once the cooldown has passed, or it would stay on the old bundle for
   * as long as the app is left open, which is the failure this whole mechanism exists to end.
   */
  const reloadedForRef = useRef<string | null>(null);
  const reloadTarget = (): string | null => {
    const target = serverBuildRef.current;
    const page = boot.current?.pageBuild ?? null;
    if (target === null || page === null || target === page) return null;
    if (target === reloadedForRef.current) return null;
    const reloadedAt = boot.current?.reloadedAt ?? null;
    if (reloadedAt !== null) {
      const since = Date.now() - reloadedAt;
      if (since >= 0 && since < BUILD_RELOAD_COOLDOWN_MS) return null;
    }
    return target;
  };
  const reloadOnto = (target: string, tabQuery: string): void => {
    reloadedForRef.current = target;
    navigation.replace(`/?build=${encodeURIComponent(target)}&at=${String(Date.now())}${tabQuery}`);
  };

  /**
   * ⭐ Board 2706 (plan D5) — `restPoint` marks REST POINT A: an EXPLICIT transition (boot, a
   * lock that landed, a PIN unlock, a sign-in, PIN setup done, a keepalive that found the session
   * gone) is a moment with nothing on screen to lose, so it may reload onto a new build — unless
   * a draft is held for this account, which the reload would destroy.
   * ⛔ NEVER the `onUnauthorized` reload: a wrong email code answers 401 MID-ENROL, and a reload
   * there would throw away the email she just typed.
   */
  const reloadAuthState = async (opts: { restPoint?: boolean } = {}): Promise<void> => {
    const res = await api.get('/api/auth/state');
    if (res.kind === 'network-error') {
      setLoadError(sharedCopy.networkError);
      return;
    }
    if (res.status !== 200 || !isAuthState(res.body)) {
      setLoadError(sharedCopy.genericError);
      return;
    }
    setLoadError(null);
    setAuth(res.body);
    if (res.body.state !== 'locked') setForceEnrol(false);
    if (opts.restPoint === true) {
      const target = reloadTarget();
      const held = heldDraft();
      const next = res.body;
      // ⭐ Board 2706 rework (R3): held work survives every state its own nurse can pass through
      // on the way back (locked, no device, PIN setup); a named person keeps it only if she is
      // the one it belongs to.
      const keeps =
        held !== null &&
        (next.state === 'active' || next.state === 'needs_pin' ? next.nurse.id === held.accountId : true);
      if (target !== null && !keeps) reloadOnto(target, '');
    }
  };
  reloadAuthStateRef.current = () => void reloadAuthState();
  const reloadAtRest = (): void => void reloadAuthState({ restPoint: true });

  useEffect(() => {
    reloadAtRest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActiveState = auth !== null && auth.state === 'active';
  const { idle, lockDue } = useIdleNudge(isActiveState, IDLE_NUDGE_MS, IDLE_LOCK_MS);

  // ⛔ T2075 M6 — EVERY LOCK GOES THROUGH `attemptLock` (the Lock button, the
  // Request sent panel's "Done, lock", the auto-lock and its retries), so
  // every lock fails closed the same way (code-review round 2 / plan-alignment
  // 5(c): the manual lock used to reload the auth state after a failed POST
  // — the server had not revoked, so the data came straight back).
  //
  // ⛔ A FAILED LOCK FAILS CLOSED, ON A SCREEN OF ITS OWN. On anything but a
  // 200 the data leaves the screen at once: `lockFailed` renders the
  // failed-lock screen (no resident name, `lockFailedCopy`), whose ONLY
  // affordance re-attempts the LOCK. It is deliberately NOT the generic
  // load-error screen: that screen's "Try again" reloads the auth state, and
  // after a failed lock POST the server has NOT revoked the session, so that
  // button put the resident list straight back on an unattended shared PC
  // (code-review round 2, finding 1, live-reproduced). While the screen is
  // up the lock is retried every LOCK_RETRY_MS (the effect below); a touch
  // does NOT lift it — the data was hidden because a lock was due, and only a
  // lock that succeeds (→ the Picker) ends it. `lockPendingRef` stops a
  // second POST while one is in flight (M6-2b).
  //
  // ⛔ A STALE RESULT IS NOT ACTED ON (code-review round 2, finding 2 — the
  // shared root cause with finding 1: the continuation acted without checking
  // it was still current). `lockGenRef` moves on every `lockDue` false flip
  // (a touch: she is here); an AUTO attempt whose generation is behind when
  // its POST FAILS is ignored, because nothing is hidden and the lock is no
  // longer due — bouncing a working nurse to an error screen would be an
  // availability regression. A 200 is honoured whatever the generation: the
  // server DID revoke, and the truth is the Picker, not a screen whose next
  // send would 401. Manual and retry attempts are never stale: she asked, or
  // the screen is already fail-closed.
  const lockPendingRef = useRef(false);
  const lockGenRef = useRef(0);
  const [lockFailed, setLockFailed] = useState(false);
  const lockFailedRef = useRef(false);
  const [lockAttempt, setLockAttempt] = useState(0);

  async function attemptLock(reason: 'auto' | 'manual' | 'retry'): Promise<void> {
    if (lockPendingRef.current) return;
    lockPendingRef.current = true;
    const gen = lockGenRef.current;
    const res = await api.post('/api/auth/lock');
    lockPendingRef.current = false;
    if (res.kind === 'response' && res.status === 200) {
      await reloadAuthState({ restPoint: true });
      return;
    }
    const stale = reason === 'auto' && gen !== lockGenRef.current && !lockFailedRef.current;
    if (stale) return;
    lockFailedRef.current = true;
    setLockFailed(true);
    setLockAttempt((n) => n + 1);
  }

  /** The manual lock (the header button, the Request sent panel). */
  const lock = (): Promise<void> => attemptLock('manual');

  useEffect(() => {
    if (!lockDue) lockGenRef.current += 1;
  }, [lockDue]);

  // The auto-lock: once per due period (the pending flag covers the in-flight case).
  useEffect(() => {
    if (!isActiveState || !lockDue) return;
    void attemptLock('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockDue, isActiveState]);

  // The retries while the failed-lock screen is up; the screen itself ends
  // when the shell leaves `active` (a lock that landed → the Picker).
  useEffect(() => {
    if (!isActiveState) {
      lockFailedRef.current = false;
      setLockFailed(false);
      return;
    }
    if (!lockFailed) return;
    const id = setTimeout(() => void attemptLock('retry'), LOCK_RETRY_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockFailed, lockAttempt, isActiveState]);

  // ⭐ T2075 M6 — THE KEEPALIVE, only while the nurse is active AND not idle.
  // `GET /api/auth/state` slides `last_active_at` through `resolveAuth` like
  // every authenticated request, so a nurse typing a long note never
  // submits into a session the server window has ended. An
  // `active` 200 is deliberately NOT written to state (`reloadAuthState` is
  // not called): the shell already knows it is active, and a re-render on
  // every tick would be a change nobody asked for. ⛔ ANY OTHER ANSWER IS
  // ACTED ON (security-review Med 2): this endpoint never 401s — a session
  // revoked out of band (a PIN reset, a deactivation, the 12 h cap) answers
  // `200 {state: 'locked'}` — so a 200 whose state is not `active` reloads
  // the screen, exactly as a 401 elsewhere does through `onUnauthorized`.
  // Cleared the moment the nudge fires, so an unattended tab never keeps its
  // own session alive (the 2073 D8 rule) — the lock at IDLE_LOCK_MS then ends it.
  // ⭐ AND ONE GET ON THE RETURN FROM IDLE (security-review Low 3): the
  // interval restarts from the return, so without this the silence between
  // contacts could reach 2·K + L − N = SESSION_IDLE_MS (the docblock at the
  // top derives it); the return GET bounds it at K + L − N, and a nurse who
  // came back to a session the server has already ended meets the Picker on
  // her first touch rather than on her next send. The D8 shape: the list
  // refetches at once on the return too.
  // ⚠️ NOT WHILE THE FAILED-LOCK SCREEN IS UP EITHER: a keepalive there would
  // slide the very session the retries are trying to revoke; with none, the
  // server's own window ends it even if every retry fails.
  const returningFromIdleRef = useRef(false);
  useEffect(() => {
    if (!isActiveState || lockFailed) {
      returningFromIdleRef.current = false;
      return;
    }
    if (idle) {
      returningFromIdleRef.current = true;
      return;
    }
    const keepalive = async (): Promise<void> => {
      const res = await api.get('/api/auth/state');
      if (res.kind === 'response' && res.status === 200 && isAuthState(res.body) && res.body.state !== 'active') {
        await reloadAuthState({ restPoint: true });
      }
    };
    if (returningFromIdleRef.current) {
      returningFromIdleRef.current = false;
      void keepalive();
    }
    const id = setInterval(() => void keepalive(), KEEPALIVE_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveState, idle, lockFailed]);

  /** The tab the active screen shows (derived; see `activeTab` below), or null when not active. */
  const liveTab: ActiveTab | null =
    activeNurseRole === null
      ? null
      : tabsForRole(activeNurseRole).includes(tab)
        ? tab
        : defaultTabForRole(activeNurseRole);
  /** The live active render: the tabs and a screen, not the cover, a failed lock or an error. */
  const liveScreen =
    auth !== null && auth.state === 'active' && loadError === null && !lockFailed && !lockDue && !forceEnrol;
  const authStateName = auth === null ? null : auth.state;

  /**
   * ⭐ Board 2706 (plan D4) — THE HOLD'S LIFECYCLE. Leaving the live render (the cover, a failed
   * lock, the Picker, a load error) stamps `heldAt` once and arms a BEST-EFFORT drop timer; the
   * restore itself is judged by the wall clock, because this timer pauses in a sleeping device.
   * A restored draft back on the live form is re-reported with `heldAt: null`, so a later remount
   * is judged against its OWN hold, never the first one.
   */
  useEffect(() => {
    const held = draftRef.current;
    if (held === null) return;
    const gone =
      (activeNurseId !== null && held.accountId !== activeNurseId) || (liveScreen && liveTab !== 'new-request');
    if (gone) {
      dropDraft();
      return;
    }
    if (!liveScreen && held.heldAt === null) {
      const heldAt = Date.now();
      held.heldAt = heldAt;
      clearTimeout(dropTimerRef.current);
      // ⛔ Board 2706 rework (round 2): KEYED ON `heldAt`, NOT ON THE OBJECT. A send that settles
      // while locked swaps in a new object carrying the same `heldAt`, and an object-keyed timer
      // then never fired: the outcome outlived the hold in memory and the Picker went on saying
      // the request was kept. Whatever is held under THIS hold is dropped; a restore (`heldAt`
      // back to null) or a later hold (a new `heldAt`) is not.
      dropTimerRef.current = setTimeout(() => {
        if (draftRef.current !== null && draftRef.current.heldAt === heldAt) dropDraft();
      }, DRAFT_HOLD_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveScreen, liveTab, authStateName, forceEnrol, activeNurseId]);

  /**
   * ⭐ Board 2706 (plan D5) — REST POINT B: the New request tab with a pristine form, not the
   * "Request sent" panel. Nothing she typed can be lost, so a new build reloads here, back to the
   * form (`tab=new-request`). The draft ref is RE-READ at the moment of the reload, so a keystroke
   * whose report has not re-rendered the shell yet still blocks it.
   * ⛔ NEVER ON ENROL OR PIN SETUP (not the live screen) AND NEVER ON ANOTHER TAB, where a list or
   * a roster edit may be mid-flight.
   */
  const onPristineForm = liveScreen && liveTab === 'new-request' && !requestSent && !formHasDraft;
  useEffect(() => {
    if (!onPristineForm || draftRef.current !== null) return;
    const target = reloadTarget();
    if (target !== null) reloadOnto(target, '&tab=new-request');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverBuild, onPristineForm]);

  /** ⭐ Board 2706 rework (R5) — the screen being shown, for the sign-in transition below. */
  const screenKind: string | null =
    auth === null || loadError !== null ? null : forceEnrol ? 'enrol' : screenForAuthState(auth);
  const prevScreenRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevScreenRef.current;
    prevScreenRef.current = screenKind;
    // Only a SIGN-IN lands focus (the Picker, the email route, PIN setup -> active): never boot,
    // a tab click or a re-render, where she is already where she means to be.
    if (screenKind !== 'active' || (prev !== 'picker' && prev !== 'enrol' && prev !== 'pin-setup')) return;
    return focusHeadingWhenShown();
  }, [screenKind]);

  /**
   * ⭐⭐ Board 2706 rework (R1) — A SEND'S PHASE, from the form's own async handler, so it lands
   * even when the lock has unmounted the form mid-send. `sending` holds the draft as sent; its
   * outcome replaces it ONLY if it is still this send's (`of`): a tab change or another account may
   * have dropped it meanwhile, and a late answer must not resurrect it. A form REMOUNTED while the
   * send was in flight (the screen left it: `heldAt` stamped) shows the sending copy with Send
   * disabled, so the settle remounts it onto the truth (`formKey`); the instance that pressed Send
   * never left and settles itself.
   */
  const [formKey, setFormKey] = useState(0);
  const onSendPhase = (phase: SendPhase): void => {
    const held = draftRef.current;
    if (phase.kind === 'sending' && phase.replaces !== undefined) {
      // The same send moved on (a resident created or put back): only while it is still this
      // send's copy, so a late report cannot resurrect what an account change dropped.
      if (held !== null && held.phase === 'sending' && held.draft === phase.replaces) {
        draftRef.current = { ...held, draft: phase.draft };
      }
      return;
    }
    if (phase.kind === 'sending') {
      if (activeNurseId === null) return;
      draftRef.current = {
        accountId: activeNurseId,
        heldAt: held !== null && held.accountId === activeNurseId ? held.heldAt : null,
        phase: 'sending',
        draft: phase.draft,
      };
      setFormHasDraft(true);
      return;
    }
    if (held === null || held.phase !== 'sending' || held.draft !== phase.of) return;
    draftRef.current =
      phase.kind === 'sent'
        ? { accountId: held.accountId, heldAt: held.heldAt, phase: 'sent', sent: phase.sent }
        : { accountId: held.accountId, heldAt: held.heldAt, phase: 'draft', draft: held.draft };
    if (held.heldAt !== null) setFormKey((k) => k + 1);
  };

  if (loadError) {
    // ⛔ WRAPPED TOO (T1033 M6). The output gate flagged this as the one
    // remaining surface rendering outside the shell, and SC-1 says the mark
    // appears on EVERY tool surface — not every screen. A nurse whose network
    // drops lands here, and it would otherwise be the only unbranded thing the
    // portal can show.
    // ⚠️ M8 CLOSED THE OTHER HALF OF THIS NOTE, which used to read "wrapping
    // does NOT define `boot-error-screen`, so the style-contract allow-list is
    // untouched; that rule is still M8's". `boot-error-screen` is GONE rather
    // than defined: it was a per-screen hook that never carried a rule, and the
    // cap it would have needed under ADR4-D6 already arrives via the
    // co-applied `.screen` (max-width: var(--measure)). Removing an unstyled
    // class is a provably zero-render change; inventing a rule for it would
    // not have been.
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
        <main>
        <div class="screen">
          {/* ⭐⭐ II-portal-#10 (T1723 M4) — THE ERROR SCREEN HAS A HEADING AGAIN,
              AND ITS MESSAGE IS NOT DRESSED AS A FIELD ERROR.
              ⛔ WHAT WAS WRONG. `main h1` was null here, so the one screen a
              nurse reaches when the network drops was the only screen in the
              product with no page heading: a screen-reader user landing on it by
              heading navigation found nothing, and the message wore
              `.field-error`, the class every REFUSED FORM CONTROL wears. The
              screen has no form and refused nothing — it failed to load — so the
              styling said something untrue about what had happened.
              ⚠️ `sharedCopy.appName` IS THE HEADING, not a new sentence: this is
              the app failing to start rather than a named operation failing, and
              `client/copy.ts` is fenced this milestone so a purpose-written
              heading cannot be added to it here. The message below says what
              happened; the heading says where she is.
              ⚠️ `role="alert"` STAYS: the paragraph is inserted after the shell
              has already rendered, so nothing else announces it. */}
          <h1>{sharedCopy.appName}</h1>
          <p class="screen-error" role="alert">
            {loadError}
          </p>
          <button type="button" class="btn btn--primary" onClick={() => void reloadAuthState()}>
            {sharedCopy.tryAgain}
          </button>
        </div>
        </main>
      </Shell>
    );
  }

  if (!auth) {
    /**
     * ⛔ WRAPPED — and the comment that used to sit here said the OPPOSITE, on
     * reasoning that was MEASURED WRONG by code-review. It claimed wrapping
     * would "flash a full brand bar in and straight back out". A jsdom
     * reconciliation probe showed the reverse: because every branch after this
     * one already returns `<Shell>`, wrapping keeps the Shell's DOM node
     * IDENTITY stable across the transition (`afterEl === beforeEl`, no
     * remount) — zero flash.
     *
     * And leaving it unwrapped was the choice with the real gap: `index.tsx`'s
     * `root.replaceChildren()` removes the boot paint on mount, so the first
     * Preact render (`auth === null`) painted a bare `<p>` with NO mark until
     * `/api/auth/state` resolved. That fires on EVERY page load, not just the
     * first, and it is not guaranteed sub-second on a slow connection.
     */
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
        <main>
          <p class="loading-notice" role="status">
            {sharedCopy.loading}
          </p>
        </main>
      </Shell>
    );
  }

  if (lockFailed && auth.state === 'active') {
    // ⛔ THE FAILED-LOCK SCREEN (code-review round 2, finding 1): fail-closed,
    // no data, no tabs, no Lock button, and its ONE affordance re-attempts the
    // lock — never `reloadAuthState`, which would put the data back.
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
        <main>
          <div class="screen">
            <h1>{sharedCopy.appName}</h1>
            <p class="screen-error" role="alert">
              {lockFailedCopy.notice}
            </p>
            <Button variant="quiet" onClick={() => void attemptLock('retry')}>
              {lockFailedCopy.retry}
            </Button>
          </div>
        </main>
      </Shell>
    );
  }

  if (lockDue && auth.state === 'active') {
    // ⭐ Board 2706 (plan D3) — THE COVER: a lock is due and its POST is on its way. Blank and
    // branded, with no data and nothing to press, so the screen is hidden for the round trip and a
    // tap that woke the device cannot land on Send. A touch on it keeps the M6 rules: before the
    // lock timer fired it locks (the wall-clock catch-up), after it the revive path lifts it.
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
        <main />
      </Shell>
    );
  }

  const screen = forceEnrol ? 'enrol' : screenForAuthState(auth);

  if (screen === 'enrol') {
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
      <main>
      <Enrol
        api={api}
        note={enrolNote}
        onSignedIn={() => {
          setEnrolNote(null);
          setForceEnrol(false);
          reloadAtRest();
        }}
      />
      </main>
      </Shell>
    );
  }

  if (screen === 'pin-setup') {
    const nurseName = auth.state === 'needs_pin' ? auth.nurse.displayName : '';
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
        <main>
          <PinSetup api={api} nurseName={nurseName} onDone={reloadAtRest} />
        </main>
      </Shell>
    );
  }

  if (screen === 'picker') {
    const roster = auth.state === 'locked' ? auth.roster : [];
    const homeName = auth.state === 'locked' && auth.home ? auth.home.name : null;
    // ⭐ Board 2706 rework (R3): say so while work is held, inside its hold, so a nurse who sees
    // the lock screen knows signing back in loses nothing. Names nothing about the request.
    const kept = draftRef.current;
    const requestKept = kept !== null && (kept.heldAt === null || Date.now() - kept.heldAt < DRAFT_HOLD_MS);
    return (
      <Shell variant="bar" label={shellCopy.brandLabel}>
      <main>
      <Picker
        api={api}
        roster={roster}
        homeName={homeName}
        onActive={reloadAtRest}
        requestKept={requestKept}
        onNeedsEnrol={(note) => {
          setEnrolNote(note ?? null);
          setForceEnrol(true);
        }}
      />
      </main>
      </Shell>
    );
  }

  // screen === 'active'
  const nurseName = auth.state === 'active' ? auth.nurse.displayName : '';
  // ⭐ T2075 M4 — the session's live role decides the strip and the form's
  // picker shape; `group` is the narrowest reading for a state this branch
  // cannot reach (the guard above returns before it).
  const role = auth.state === 'active' ? auth.nurse.role : 'group';
  /**
   * ⭐⭐ T2173 rework 1b — THE ACTIVE TAB IS DERIVED, NOT JUST CORRECTED LATER, and
   * that is what stops the stale request rather than merely tidying the landing.
   *
   * ⛔ THE RESIDUE REWORK 1 LEFT: resetting the tab in an effect fixes where the
   * nurse ENDS UP, but an effect runs AFTER the render that scheduled it. So on the
   * one render where `auth` had already flipped to the group nurse and `tab` was
   * still `staff`, the Staff screen MOUNTED — and its mount effect fired
   * `/api/admin/staff` and `/api/admin/accounts`, which the server 403s and the
   * console logs twice. Captain's re-drive caught exactly those two GETs. No data
   * leaked; the request should never have been made.
   *
   * ⛔ A ROLE THAT IS NOT OFFERED A TAB NEVER RENDERS ITS SCREEN — not for one
   * frame. `tabsForRole` was always the authority on what a role may see; this line
   * makes the screen switch read it instead of trusting shell state that outlives
   * the person. The effect below still exists, because a stale tab the new role IS
   * allowed (an admin on `my-requests`, then reception) needs moving to that role's
   * own default, which a membership test alone would leave alone.
   */
  const activeTab: ActiveTab = liveTab ?? defaultTabForRole(role);

  /**
   * ⭐ Board 2706 (plan D4) — the draft the New request form starts from: the held one, for the
   * SAME account and inside its hold by the wall clock, else none (a fresh form, which reports
   * `null` and so drops whatever was held). Read by the form at mount only.
   * ⛔ A `group` ACCOUNT IS SHARED BY A FLOOR, so whoever unlocks it may not be who typed: the
   * requester pick is cleared and she must name herself, so a request cannot leave under the first
   * nurse's name unnoticed (`requested_by_staff_id` is the audit record of who asked).
   * ⛔ AND THE TWO TICKS GO WITH IT (security review Low 2): the Cash / card double-check and the
   * roster-rewrite consent are statements by the person who ticked them, so the next person must
   * make them herself rather than inherit them. Everything else she typed is kept.
   */
  const stored = draftRef.current;
  const held =
    stored !== null && stored.accountId === activeNurseId && (stored.heldAt === null || Date.now() - stored.heldAt < DRAFT_HOLD_MS)
      ? stored
      : null;
  // ⭐ Board 2706 rework (R1): a send that settled while the form was away comes back as its
  // panel; one still in flight comes back as the sending copy with Send disabled.
  const initialSent: RequestSentFacts | undefined = held?.phase === 'sent' ? held.sent : undefined;
  const initialDraft: RequestFormDraft | undefined =
    held === null || held.phase === 'sent'
      ? undefined
      : held.phase === 'draft' && role === 'group'
        ? {
            ...held.draft,
            values: {
              ...held.draft.values,
              requestedByStaffId: '',
              requesterGroup: '',
              // board 2732 M5: the typed name and a staged add go with the pick,
              // so the next nurse at the shared tablet names herself.
              requesterQuery: '',
              requesterAdd: false,
              payerConfirmed: false,
              updateRoster: false,
            },
          }
        : held.draft;
  const onDraftChange = (draft: RequestFormDraft | null): void => {
    // ⛔ Board 2706 rework (R1): while a send is in flight the held copy IS what was sent, and the
    // form's own reports (a remount, the created resident landing in its state) must not turn it
    // back into a plain draft that a later unlock would offer to send again.
    if (draftRef.current?.phase === 'sending') return;
    if (draft === null || activeNurseId === null) {
      dropDraft();
      return;
    }
    draftRef.current = { accountId: activeNurseId, heldAt: null, phase: 'draft', draft };
    setFormHasDraft(true);
  };

  /** The pressed tab, or none while the Request sent panel is up (II-portal-#27). */
  const pressedTab: ActiveTab | null = requestSent ? null : activeTab;
  const homeName = auth.state === 'active' && auth.home ? auth.home.name : null;

  return (
    <Shell
      variant="bar"
      label={shellCopy.brandLabel}
      aside={
        // ⚠️ ONE element, not a fragment: `.app-header` is space-between, so
        // separate children would spread across the bar instead of grouping.
        <div class="app-header-side">
          <div class="app-header-info">
            {/* ⚠️ `title` is the SIGHTED half of the truncation (T1717 M7): the
                CSS ellipsis clips the render, never the DOM text, so the
                accessible name is already the full home name — this is what
                lets a sighted nurse read the rest of it. */}
            {homeName && (
              <span class="app-header-home" title={homeName}>
                {homeName}
              </span>
            )}
            {/* ⚠️ `title` HERE TOO — T1717 M7, code-review. The nurse name
                truncates under the SAME rule as the home name above (both spans
                share the `overflow/text-overflow/nowrap` block), so leaving the
                fallback on one of them made the affordance look deliberate when
                it was just the span the reviewer happened to be looking at. */}
            <span class="app-header-nurse" title={nurseName}>
              {nurseName}
            </span>
          </div>
          <button type="button" class="btn btn--secondary" onClick={() => void lock()}>
            {shellCopy.lockButton}
          </button>
        </div>
      }
    >

      {/* ⭐ T2075 M6 — the nudge names its consequence now ("…or it locks in a
          minute"): the same timer locks the screen `IDLE_LOCK_MS` minus
          `IDLE_NUDGE_MS` after this renders (one minute, so the sentence stays
          true at the board 2706 figures), and a touch clears both. */}
      {idle && (
        <p class="idle-nudge" role="status">
          {shellCopy.idleNudge}
        </p>
      )}

      {/* ⭐ T1749 M4 — THE TABS ARE `Button`'s `tab` VARIANT NOW (DS-5 drift 3).
          The rendered look is unchanged: this surface's underline is the one the
          sitting ruled, so `.btn--tab` in `@ecmb/ui/styles/components.css` IS
          this strip's old `.tab-btn` rule, and the kiosk's two tab strips moved
          onto it rather than the other way round.
          ⛔ THE CHOSEN TAB IS `aria-pressed` AND NOTHING ELSE. There is no
          `--active` class any more: the sheet paints
          `.btn--tab[aria-pressed='true']`, so a tab cannot look chosen without
          announcing it. `app-state.spec.tsx` grades the single pressed tab and
          is unchanged — it reads the attribute, which is the point. */}
      {/* ⭐ T2075 M4 (plan A8, D13) — THE STRIP RENDERS PER ROLE, from
          `tabsForRole` (a pure list, graded as a per-role matrix in
          `app-state.spec.tsx`): the three base tabs for everyone, `Requests`
          for reception and admin, `Staff` for admin (T2075 M5).
          ⭐ T1717 M3 — the third tab sits AFTER "My requests" (plan M3). The
          strip is allowed to WRAP onto a second row rather than shortening
          these labels: at 320px three tabs on one row would be ~107px each
          against a ~142px label, and abbreviating a nurse-facing label to
          fit a phone is the wrong trade on a screen this audience already
          bounced off a competitor for. See `.app-tabs .btn--tab` in app.css,
          where the 160px floor that decides the wrap lives. */}
      <nav class="app-tabs">
        {tabsForRole(role).map((t) => (
          <Button
            key={t}
            variant="tab"
            ariaPressed={pressedTab === t}
            onClick={() => {
              // ⭐ Board 2706: an ACTUAL tab change leaves the form, and with it the draft;
              // re-pressing New request is not a change and keeps it.
              if (t !== activeTab) dropDraft();
              setTab(t);
            }}
          >
            {TAB_LABELS[t]}
          </Button>
        ))}
      </nav>

      {/* ⛔ MUTUALLY EXCLUSIVE, AND STILL A SINGLE EXPRESSION. Each screen
          mounts fresh when its tab is chosen and unmounts when it is left, so
          no screen holds a stale roster or a half-typed form behind another
          one. `app-state.spec.tsx` grades the exclusivity and the single
          pressed tab. */}
      <main>
        {activeTab === 'new-request' ? (
          // ⭐ T2075 M3 — the session's role shapes the requester picker (one
          // select for a `group` account; group then name for reception and
          // admin). `auth.state` is `active` on this branch by the guard above.
          <RequestForm
            key={formKey}
            api={api}
            role={role}
            // board 2732 M5: a ward account's group, for the Add hint when its
            // roster is empty (`meta.staff` then has no row to read it from).
            groupLabel={auth.state === 'active' ? auth.nurse.groupLabel : null}
            onLock={() => void lock()}
            onSentChange={setRequestSent}
            initialDraft={initialDraft}
            onDraftChange={onDraftChange}
            onSendPhase={onSendPhase}
            sendPending={held?.phase === 'sending'}
            initialSent={initialSent}
            accountId={activeNurseId ?? undefined}
          />
        ) : activeTab === 'my-requests' ? (
          // ⛔ T2073 M2 (D8) — `live` is the idle nudge inverted: the list's
          // 15 s refresh runs only while the nurse is attending the tab, because
          // every authenticated request slides the session's `last_active_at`
          // (`auth-repo.ts`) and a poll on an unattended tab would keep alive
          // the session the idle window (`SESSION_IDLE_MS`, 660 s since board
          // 2706) exists to end. The nudge is the client-side idle signal, so it
          // gates the tick; the same timer's second stage is the auto-lock.
          <MyRequests api={api} live={!idle} />
        ) : activeTab === 'my-residents' ? (
          <MyResidents api={api} />
        ) : activeTab === 'home-requests' ? (
          // ⭐ T2075 M4 — the home-wide list; only a reception or admin session
          // is ever offered the tab (`tabsForRole`), and the route answers 403
          // to anyone else regardless. ⭐ board 2725 / 2732 R-11 — `live` exactly
          // as My requests: it refreshes on its own while attended and stops once
          // the idle nudge shows, so an unattended tab never slides the session.
          <HomeRequests api={api} live={!idle} />
        ) : (
          // ⭐ T2075 M5 — the admin's Staff tab; only an admin session is ever
          // offered it (`tabsForRole`), and every `/api/admin/*` route answers
          // 403 to anyone else regardless.
          <Staff api={api} />
        )}
      </main>
    </Shell>
  );
}
